import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ForgeDialog } from '@tylertech/forge-react';
import { TylerEmbeddedApplicationHost, MessageType } from '@tylertech/tyler-embedded-application';
import { FeatureFlags } from '@socrata/platform/feature-flags';

export interface ProcessDiscoveryEmbedModalProps {
  showModal?: boolean;
  url: string;
  onDismiss: () => void;
}

const ProcessDiscoveryEmbedModal = ({ url, onDismiss, showModal }: ProcessDiscoveryEmbedModalProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [, setLoadCount] = useState(0);
  const host = useMemo(() => new TylerEmbeddedApplicationHost(), []);
  const urlWithParams = host.createUri(url);
  const closeModal = useCallback(() => {
    host.deregisterMessageListener(MessageType.Close);
    onDismiss();
  }, [host, onDismiss]);

  useEffect(() => {
    if (containerRef.current) {
      host.load(urlWithParams, {}, containerRef.current);
      host.registerMessageListener(MessageType.Close, closeModal);
    }
  }, [host, urlWithParams, closeModal]);

  // This is a workaround in order to handle okta redirects in the iframe.
  // We should NOT be doing side effects within a setState callback,
  // but this is a temporary solution until we can refactor the authentication flow.
  useEffect(() => {
    const iframe = containerRef.current?.querySelector('iframe');
    const handleLoad = () => {
      setLoadCount((prev) => {
        const newCount = prev + 1;

        // If iframe loads more than once, assume a redirect
        if (newCount > 1) {
          console.warn('Possible redirect detected. Opening in new tab...');
          closeModal(); // Close the modal
          window.open(urlWithParams, '_blank');
        }

        return newCount;
      });
    };

    if (FeatureFlags.valueOrDefault('enable_tir_actions_auth_workaround', false) && iframe) {
      iframe.addEventListener('load', handleLoad);
    }

    return () => {
      if (FeatureFlags.valueOrDefault('enable_tir_actions_auth_workaround', false) && iframe) {
        iframe.removeEventListener('load', handleLoad);
      }
    };
  }, [closeModal, urlWithParams]);

  return (
    <ForgeDialog open={showModal} on-forge-dialog-close={closeModal}>
      <div
        ref={containerRef}
        className="process-discovery-modal-body"
        data-testid="process-discovery-modal-body"
      />
    </ForgeDialog>
  );
};

export default ProcessDiscoveryEmbedModal;
